import { Menu, MenuItem } from '@material-ui/core';
import clsx from 'clsx';
import { Body, Button } from 'copilot-design-system';
import * as React from 'react';

import { useResizeObserver } from 'src/hooks/useResizeObserver';

interface Tab<T> {
  key: T;
  label: string | React.ReactNode;
  disabled?: boolean;
  icon?: React.ComponentType;
}

const MORE_TABS_BUTTON_WIDTH = 100;

interface ResponsiveTabsProps<T> {
  tabs: Array<Tab<T>>;
  activeTabKey: T | undefined;
  onTabChange: (newValue: T) => void;
  overflow?: 'scroll' | 'truncate';
  className?: string;
  classes?: {
    tabButton?: string;
  };
}

function getActiveItemClassName(isActive: boolean) {
  return [
    'border-[0]',
    'border-b',
    'border-solid',
    isActive ? 'border-gray-500' : 'border-transparent',
    isActive ? 'text-primary' : 'text-secondary',
  ].join(' ');
}

/**
 * Base Tabs component renders tabs header
 * @returns {React.ReactElement}
 */
export const ResponsiveTabs = <T,>({
  activeTabKey,
  onTabChange,
  tabs,
  overflow,
  className,
  classes,
}: ResponsiveTabsProps<T>) => {
  const [tabGroupings, setTabGroupings] = React.useState<{
    primary: Tab<T>[];
    overflow: Tab<T>[];
  }>({
    primary: [],
    overflow: [],
  });
  const [isOverflowMenuOpen, setIsOverflowMenuOpen] = React.useState(false);
  const fallbackTab = tabs.length ? tabs.at(0)?.key : '';
  const activeTab = activeTabKey || fallbackTab;
  const overflowRef = React.useRef<HTMLDivElement | null>(null);
  const { ref: containerRef, rect: containerRect } =
    useResizeObserver<HTMLDivElement>();
  const hiddenContainerRef = React.useRef<HTMLDivElement | null>(null);

  const renderTabButton = (
    tab: Tab<T>,
    options: {
      isHidden?: boolean;
      onClick?: () => void;
    } = {},
  ) => {
    const { isHidden = false, onClick } = options;
    const labelText =
      typeof tab.label === 'string' ? tab.label : String(tab.label);

    return (
      <div key={String(tab.key)} data-tab-key={tab.key} className="h-full">
        <Button
          className={[
            'whitespace-nowrap rounded-none',
            'h-full px-2',
            getActiveItemClassName(activeTab === tab.key),
            classes?.tabButton,
          ].join(' ')}
          data-testid={isHidden ? undefined : `tab-${String(tab.key)}`}
          onClick={onClick}
          label={labelText}
          variant="text"
          tabIndex={isHidden ? -1 : undefined}
        />
      </div>
    );
  };

  React.useEffect(() => {
    const calculatePrimaryTabs = () => {
      if (!containerRef.current || !hiddenContainerRef.current) {
        return;
      }

      // when overflow is set to scroll, we want to show all tabs
      // without truncating the tabs into dropdown.
      if (overflow === 'scroll') {
        setTabGroupings({
          primary: tabs,
          overflow: [],
        });
        return;
      }

      const { right: containerRight } =
        containerRef.current.getBoundingClientRect();

      if (!containerRight && typeof containerRight !== 'number') {
        return;
      }

      // Query tabs from the hidden container for measurement
      const tabEls = Array.from(
        hiddenContainerRef.current.querySelectorAll('[data-tab-key]'),
      );

      let lastFittingIndex = tabEls.length;

      for (let i = 0; i < tabEls.length; i++) {
        const tabEl = tabEls.at(i);
        const { right: tabRight } = tabEl?.getBoundingClientRect() ?? {};
        if (!tabRight && typeof tabRight !== 'number') return;

        // Calculate if this tab + "More" button would fit
        if (containerRight < tabRight + MORE_TABS_BUTTON_WIDTH) {
          lastFittingIndex = i - 1;
          break;
        }
      }

      // Ensure at least one tab is always visible
      if (lastFittingIndex < 1) {
        lastFittingIndex = 1;
      }

      setTabGroupings({
        primary: tabs.slice(0, lastFittingIndex),
        overflow: tabs.slice(lastFittingIndex),
      });
    };

    calculatePrimaryTabs();
  }, [containerRef, hiddenContainerRef, containerRect, tabs, overflow]);

  const handleOverflowTabChange = (tabKey: T) => {
    onTabChange(tabKey);
    setIsOverflowMenuOpen(false);
  };

  React.useEffect(() => {
    if (tabs.length && tabGroupings.primary.length === 0) {
      setTabGroupings({
        primary: tabs,
        overflow: [],
      });
    }
  }, [tabs, tabGroupings]);

  // Implements a notion style tab rendering
  // In case of a lot of tabs, we want the active tab to always render
  // instead of being in the list.
  const effectiveTabGroupings = React.useMemo(() => {
    // Keep current behavior when there is no overflow.
    if (tabGroupings.overflow.length === 0) {
      return tabGroupings;
    }

    // Check if active tab is already visible in primary tabs
    const isActiveInPrimary = tabGroupings.primary.some(
      (tab) => tab.key === activeTab,
    );

    // If active tab is already visible, keep the width-based calculation
    if (isActiveInPrimary) {
      return tabGroupings;
    }

    // Active tab is in overflow - swap it into primary
    const activeTabItem = tabs.find((tab) => tab.key === activeTab);
    if (!activeTabItem) {
      return tabGroupings;
    }

    // Replace the last primary tab with the active tab to maintain similar width
    // Keep at least the first tab always visible
    const newPrimary = [...tabGroupings.primary];
    if (newPrimary.length > 1) {
      // Replace the last tab (not the first) with the active tab
      const removedTab = newPrimary.pop()!;
      newPrimary.push(activeTabItem);

      // Build new overflow: removed tab + original overflow minus active tab
      const newOverflow = [
        removedTab,
        ...tabGroupings.overflow.filter((tab) => tab.key !== activeTab),
      ];

      return {
        primary: newPrimary,
        overflow: newOverflow,
      };
    }

    // Only one tab in primary, add active tab as second
    const [firstPrimaryTab] = newPrimary;
    if (!firstPrimaryTab) {
      return tabGroupings;
    }

    return {
      primary: [firstPrimaryTab, activeTabItem],
      overflow: tabGroupings.overflow.filter((tab) => tab.key !== activeTab),
    };
  }, [activeTab, tabGroupings, tabs]);

  return (
    <>
      {/* Hidden container for measuring all tabs */}
      <div
        ref={hiddenContainerRef}
        className="invisible fixed flex gap-2"
        aria-hidden="true"
      >
        {tabs.map((tab) => renderTabButton(tab, { isHidden: true }))}
      </div>

      {/* Visible container */}
      <div
        className={[
          'min-w-[250px]',
          'flex-1',
          'flex',
          'px-3',
          'sm:px-0',
          'gap-3',
          'items-center',
          className,
        ].join(' ')}
        ref={containerRef}
      >
        <div
          className={[
            'flex',
            'flex-nowrap',
            'gap-2',
            'items-center',
            'h-full',
          ].join(' ')}
        >
          {effectiveTabGroupings.primary.map((tab) =>
            renderTabButton(tab, {
              onClick: () => onTabChange(tab.key),
            }),
          )}
        </div>

        {effectiveTabGroupings.overflow.length > 0 && (
          <>
            <div
              ref={overflowRef}
              key="more-tabs"
              className={[
                'h-full',
                'flex',
                'items-center',
                getActiveItemClassName(
                  Boolean(
                    activeTab &&
                      effectiveTabGroupings.overflow
                        .map((tab) => tab.key)
                        .includes(activeTab),
                  ),
                ),
              ].join(' ')}
            >
              <Button
                className={clsx('text-gray-500 whitespace-nowrap h-full', {
                  'bg-primary-hover': isOverflowMenuOpen,
                })}
                label={`${effectiveTabGroupings.overflow.length} more`}
                onClick={() => {
                  setIsOverflowMenuOpen(!isOverflowMenuOpen);
                }}
                suffixIcon="ChevronDown"
                variant="text"
              />
              <Menu
                open={isOverflowMenuOpen}
                anchorEl={overflowRef.current}
                onClose={() => setIsOverflowMenuOpen(false)}
                getContentAnchorEl={null}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
              >
                {effectiveTabGroupings.overflow.map(
                  ({ key, label, icon: TabIcon }) => (
                    <MenuItem
                      className={
                        key === activeTab ? 'bg-gray-200 hover:bg-gray-200' : ''
                      }
                      key={String(key)}
                      value={String(key)}
                      onClick={() => handleOverflowTabChange(key)}
                    >
                      {TabIcon && (
                        <div className="mr-2 [&>svg]:w-3 [&>svg]:h-3">
                          <TabIcon />
                        </div>
                      )}
                      <Body size="sm">{label}</Body>
                    </MenuItem>
                  ),
                )}
              </Menu>
            </div>
          </>
        )}
      </div>
    </>
  );
};
